// src/pages/cadastros/LigacoesPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Form, InputGroup, Row, Col,
  Modal, Spinner, Alert, Pagination, Badge
} from 'react-bootstrap';
import {
  FaSearch, FaPlus, FaEdit, FaTrash, FaExclamationTriangle, FaSave
} from 'react-icons/fa';
import useSortable from '../../hooks/useSortable';
import SortableTh from '../../components/SortableTh';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../../service/api';

interface Pessoa {
  PES_CODIGO: number;
  PES_NOME: string;
}

interface Ligacao {
  LIG_CODIGO: number;
  PES_CODIGO: number;
  LIG_HIDROMETRO: string;
  LIG_ENDERECO: string;
  LIG_SITUACAO: string;
  LIG_FICHA: number;
  LIG_TAXAFIXA: number;
  LIG_CONTAS_AGRUPADAS: number;
  PES_NOME?: string; // vindo de JOIN eventual
}

interface LigacaoFormValues {
  PES_CODIGO: number;
  LIG_HIDROMETRO: string;
  LIG_ENDERECO: string;
  LIG_SITUACAO: string;
  LIG_FICHA: number;
  LIG_TAXAFIXA: number;
  LIG_CONTAS_AGRUPADAS: number;
}

const ITEMS_PER_PAGE = 20;

const validationSchema = Yup.object({
  PES_CODIGO: Yup.number().required('Pessoa é obrigatória').min(1, 'Selecione uma pessoa'),
  LIG_HIDROMETRO: Yup.string().max(20),
  LIG_ENDERECO: Yup.string().required('Endereço é obrigatório').max(200),
  LIG_SITUACAO: Yup.string().required('Situação é obrigatória').max(10),
  LIG_FICHA: Yup.number().min(0),
  LIG_TAXAFIXA: Yup.number().min(0),
  LIG_CONTAS_AGRUPADAS: Yup.number().min(0),
});

const emptyForm: LigacaoFormValues = {
  PES_CODIGO: 0, LIG_HIDROMETRO: '', LIG_ENDERECO: '',
  LIG_SITUACAO: 'A', LIG_FICHA: 0, LIG_TAXAFIXA: 0, LIG_CONTAS_AGRUPADAS: 0,
};

const getSituacaoBadge = (situacao: string) => {
  const sit = (situacao || '').trim();
  if (sit === 'Ativo') return <Badge bg="success">Ativo</Badge>;
  if (sit === 'Inativo') return <Badge bg="secondary">Inativo</Badge>;
  return <Badge bg="warning">{situacao}</Badge>;
};

const LigacoesPage: React.FC = () => {
  const [ligacoes, setLigacoes] = useState<Ligacao[]>([]);
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSituacao, setFilterSituacao] = useState('Ativo');
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [currentLigacao, setCurrentLigacao] = useState<Ligacao | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ligacaoToDelete, setLigacaoToDelete] = useState<Ligacao | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchLigacoes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [ligRes, pesRes] = await Promise.all([
        api.get('/ligacoes'),
        api.get('/pessoas'),
      ]);
      const pessoasMap = new Map<number, string>(
        pesRes.data.map((p: Pessoa) => [p.PES_CODIGO, p.PES_NOME])
      );
      const enriched = ligRes.data.map((l: Ligacao) => ({
        ...l,
        PES_NOME: pessoasMap.get(l.PES_CODIGO) ?? '',
      }));
      setLigacoes(enriched);
      setPessoas(pesRes.data.sort((a: Pessoa, b: Pessoa) =>
        a.PES_NOME.localeCompare(b.PES_NOME, 'pt-BR')
      ));
      setCurrentPage(1);
    } catch {
      setError('Não foi possível carregar as ligações. Verifique a conexão.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLigacoes(); }, [fetchLigacoes]);

  const filtered = ligacoes.filter(l => {
    const situacao = (l.LIG_SITUACAO || '').trim();
    return (filterSituacao === '' || situacao === filterSituacao) &&
      (l.LIG_ENDERECO?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.PES_NOME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(l.LIG_CODIGO).includes(searchTerm));
  });

  const { sortedData: sortedLigacoes, sortKey, sortDir, handleSort } = useSortable<Ligacao>(filtered, 'PES_NOME');
  const paginated = sortedLigacoes.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const handleSubmit = async (
    values: LigacaoFormValues,
    { resetForm }: FormikHelpers<LigacaoFormValues>
  ) => {
    try {
      setFormSubmitting(true);
      if (modalMode === 'create') {
        await api.post('/ligacoes', values);
      } else {
        await api.put(`/ligacoes/${currentLigacao!.LIG_CODIGO}`, values);
      }
      setShowModal(false);
      resetForm();
      fetchLigacoes();
    } catch {
      setError('Não foi possível salvar. Tente novamente.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!ligacaoToDelete) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/ligacoes/${ligacaoToDelete.LIG_CODIGO}`);
      setShowDeleteModal(false);
      setLigacaoToDelete(null);
      fetchLigacoes();
    } catch {
      setError('Não foi possível excluir. Tente novamente.');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Ligações</h1>
        <Button variant="primary" onClick={() => { setModalMode('create'); setCurrentLigacao(null); setShowModal(true); }}>
          {FaPlus({ className: "me-2" })} Nova Ligação
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{FaExclamationTriangle({ className: "me-2" })}{error}</Alert>}

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>{FaSearch({})}</InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por endereço, pessoa ou código..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterSituacao}
                onChange={e => { setFilterSituacao(e.target.value); setCurrentPage(1); }}
              >
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
                <option value="">Todos</option>
              </Form.Select>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end align-items-center">
              <span className="text-muted">Total: {filtered.length}</span>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-3">Carregando...</p></div>
          ) : filtered.length === 0 ? (
            <Alert variant="info">Nenhuma ligação encontrada.</Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead className="table-light">
                    <tr>
                      <SortableTh<Ligacao> label="Código" sortKey="LIG_CODIGO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Pessoa" sortKey="PES_NOME" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Hidrômetro" sortKey="LIG_HIDROMETRO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Endereço" sortKey="LIG_ENDERECO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Situação" sortKey="LIG_SITUACAO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Ficha" sortKey="LIG_FICHA" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Ligacao> label="Taxa Fixa" sortKey="LIG_TAXAFIXA" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(l => (
                      <tr key={l.LIG_CODIGO}>
                        <td>{l.LIG_CODIGO}</td>
                        <td>{l.PES_NOME}</td>
                        <td>{l.LIG_HIDROMETRO}</td>
                        <td>{l.LIG_ENDERECO}</td>
                        <td>{getSituacaoBadge(l.LIG_SITUACAO)}</td>
                        <td>{l.LIG_FICHA}</td>
                        <td>{l.LIG_TAXAFIXA ? 'Sim' : 'Não'}</td>
                        <td>
                          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => { setModalMode('edit'); setCurrentLigacao(l); setShowModal(true); }} title="Editar">{FaEdit({})}</Button>
                          <Button variant="outline-danger" size="sm" onClick={() => { setLigacaoToDelete(l); setShowDeleteModal(true); }} title="Excluir">{FaTrash({})}</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {arr[idx - 1] && p - arr[idx - 1] > 1 && <Pagination.Ellipsis disabled />}
                          <Pagination.Item active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                        </React.Fragment>
                      ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal Criar/Editar */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'create' ? 'Nova Ligação' : 'Editar Ligação'}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={currentLigacao
            ? { PES_CODIGO: currentLigacao.PES_CODIGO, LIG_HIDROMETRO: currentLigacao.LIG_HIDROMETRO, LIG_ENDERECO: currentLigacao.LIG_ENDERECO, LIG_SITUACAO: currentLigacao.LIG_SITUACAO, LIG_FICHA: currentLigacao.LIG_FICHA, LIG_TAXAFIXA: currentLigacao.LIG_TAXAFIXA, LIG_CONTAS_AGRUPADAS: currentLigacao.LIG_CONTAS_AGRUPADAS }
            : emptyForm}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fs }) => (
            <Form onSubmit={fs}>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Pessoa *</Form.Label>
                  <Form.Select name="PES_CODIGO" value={values.PES_CODIGO} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.PES_CODIGO && !!errors.PES_CODIGO}>
                    <option value={0}>Selecione uma pessoa</option>
                    {pessoas.filter(p =>
                      ligacoes.some(l => l.PES_CODIGO === p.PES_CODIGO && (l.LIG_SITUACAO || '').trim() === 'Ativo')
                    ).map(p => <option key={p.PES_CODIGO} value={p.PES_CODIGO}>{p.PES_NOME}</option>)}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.PES_CODIGO}</Form.Control.Feedback>
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Hidrômetro</Form.Label>
                      <Form.Control name="LIG_HIDROMETRO" value={values.LIG_HIDROMETRO} onChange={handleChange} onBlur={handleBlur} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Situação *</Form.Label>
                      <Form.Select name="LIG_SITUACAO" value={values.LIG_SITUACAO} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.LIG_SITUACAO && !!errors.LIG_SITUACAO}>
                        <option value="A">Ativa</option>
                        <option value="I">Inativa</option>
                        <option value="S">Suspensa</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">{errors.LIG_SITUACAO}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Endereço *</Form.Label>
                  <Form.Control name="LIG_ENDERECO" value={values.LIG_ENDERECO} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.LIG_ENDERECO && !!errors.LIG_ENDERECO} />
                  <Form.Control.Feedback type="invalid">{errors.LIG_ENDERECO}</Form.Control.Feedback>
                </Form.Group>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ficha</Form.Label>
                      <Form.Control type="number" name="LIG_FICHA" value={values.LIG_FICHA} onChange={handleChange} onBlur={handleBlur} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Taxa Fixa</Form.Label>
                      <Form.Check type="checkbox" name="LIG_TAXAFIXA" label="Sim" checked={!!values.LIG_TAXAFIXA} onChange={e => handleChange({ target: { name: 'LIG_TAXAFIXA', value: e.target.checked ? 1 : 0 } } as any)} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contas Agrupadas</Form.Label>
                      <Form.Check type="checkbox" name="LIG_CONTAS_AGRUPADAS" label="Sim" checked={!!values.LIG_CONTAS_AGRUPADAS} onChange={e => handleChange({ target: { name: 'LIG_CONTAS_AGRUPADAS', value: e.target.checked ? 1 : 0 } } as any)} />
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button variant="primary" type="submit" disabled={formSubmitting}>
                  {formSubmitting ? 'Salvando...' : <>{FaSave({ className: "me-2" })}Salvar</>}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Modal Excluir */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Exclusão</Modal.Title></Modal.Header>
        <Modal.Body className="text-center py-4">
          {FaExclamationTriangle({ size: 48, className: "text-danger mb-3" })}
          <p>Excluir ligação <strong>#{ligacaoToDelete?.LIG_CODIGO}</strong> — {ligacaoToDelete?.LIG_ENDERECO}?</p>
          <p className="text-danger small">Esta ação não pode ser desfeita.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteLoading}>{deleteLoading ? 'Excluindo...' : 'Excluir'}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LigacoesPage;
